import { FormErrors } from 'KnmPackage/MainModule/types/FormErrors';
import { KnmSubjectTypesEnum } from 'KnmErknmPackage/ErknmSubjectModule/components/ErknmSubjectInfo';
import { IApplicationConditionSubject } from 'APIPackage/AccountModule/interfaces/IApplicationConditionSubject';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import {
  APPLICATION_CONDITION_SUBJECT_REQUIRED,
  INN_ERROR,
  KPP_ERROR,
  MAX_CHARACTERS_ERROR,
  MIN_CHARACTERS_ERROR,
  OGRN_LENGTH_ERROR,
  RAFP_NZA_ERROR,
  REQUIRED_ERROR,
  UNALLOWED_CHARACTERS_ERROR,
} from 'CorePackage/MainModule/constants/validationErrors';
import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { CHECKING_IT_ACCREDITATION } from 'CorePackage/MainModule/constants/notices';
import { makeIsNormalInteger } from 'CorePackage/MainModule/validators/isNormalInteger';
import { makeMaxCharactersValidator } from 'CorePackage/MainModule/validators/maxCharacters';
import { makeIsOgrn } from 'CorePackage/MainModule/validators/isOgrn';
import { makeIsErknmInn } from 'CorePackage/MainModule/validators/makeIsErknmInn';
import { isDigitsCountCorrect } from 'CorePackage/MainModule/validators/isDigitsCountCorrect';
import { validateRafpNzaStatus } from 'CorePackage/MainModule/validators/validateRafpNzaStatus';
import { makeSnilsValidation } from 'CorePackage/MainModule/validators/isValidSnils';
import { makeMinCharactersValidator } from 'CorePackage/MainModule/validators/minCharacters';
import { KnmReasonTypeNameEnum } from 'KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';

import {
  getDecisionApplicationConditionSubjectsOptions,
  getDecisionIsPrescriptionFailed,
  getDecisionIsPrescriptionOldRules,
} from '../selectors/decisionSelectors';
import { IDecisionForm } from '../interfaces/IDecisionForm';
import { IDecisionOrganizationForm } from '../interfaces/IDecisionOrganizationForm';
import { getDecisionActValidate } from './getDecisionActValidate';
import { getDecisionResultDecisionValidator } from './getDecisionResultDecisionValidator';

export interface IDecisionOrganizationFormValidator {
  item: IDecisionOrganizationForm;
  values: IDecisionForm;
  isSupervisor?: boolean;
  isWatchOnly?: boolean;
  isNewDecision?: boolean;
  isFounderValidate?: boolean;
  isDecisionStatusFillInProcess?: boolean;
  isDecisionStatusReadyForApprove?: boolean;
  settings: IAdminSetting[] | undefined;
  state: IRootState;
}

export const getDecisionOrganizationValidate = ({
  item,
  values,
  isNewDecision,
  isSupervisor,
  isWatchOnly,
  isFounderValidate,
  isDecisionStatusFillInProcess,
  isDecisionStatusReadyForApprove,
  settings,
  state,
}: IDecisionOrganizationFormValidator): FormErrors<IDecisionOrganizationForm> | string => {
  const { basisDocumentDateTime, created, reasons, reasonRequirements } = values;

  const isUseEvidence = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_EVIDENCE_4_1_25_STARTS_FROM,
  );

  const isEnablePrescriptionCheck = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_105_166_25_4_1_30_STARTS_FROM,
  );

  const isUse5234153 = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_523_4_1_53_STARTS_FROM,
  );

  const isUse4151 = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_223_4_1_16_STARTS_FROM,
  );

  const isLater4111ForDecision = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.DECISION_SUBJECT_TYPE_4_1_1_START_FROM,
  );

  // Валидация акта требует поля повторного нарушения там, где интерфейс их не рисует.
  const isRepeatValidationChecking =
    (reasons?.[0].reason?.type?.name === KnmReasonTypeNameEnum.PRESCRIPTION_CHECK ||
      getDecisionIsPrescriptionFailed(state) === true) &&
    getDecisionIsPrescriptionOldRules(state) === false;

  const applicationConditionSubjectsOptions = getDecisionApplicationConditionSubjectsOptions(state);

  const isOrganizationNameFieldEnabledWithoutFiz = (itemIzFiz: boolean, isNewOrganizationTemplate?: boolean) =>
    !isWatchOnly &&
    isSupervisor &&
    (isNewOrganizationTemplate ||
      isNewDecision ||
      isDecisionStatusFillInProcess ||
      (isDecisionStatusReadyForApprove && itemIzFiz));
  // &&    !(isKnmPlanStatusConsidered && knmDataApprovedType === ApprovedTypesEnum.APPROVED && !!knmDataNote);

  const isFIZ = item.organizationType?.label === KnmSubjectTypesEnum.FIZ;
  const isFIUR = item.organizationType?.label === KnmSubjectTypesEnum.FIUR;
  const isIUR = item.organizationType?.label === KnmSubjectTypesEnum.IUR;
  const isUR = item.organizationType?.label === KnmSubjectTypesEnum.UR;
  const isIP = item.organizationType?.label === KnmSubjectTypesEnum.IP;
  const isIFIZ = item.organizationType?.label === KnmSubjectTypesEnum.IFIZ;
  let foundApplCondSubject: IApplicationConditionSubject | undefined;
  const ApplCondSubLength = applicationConditionSubjectsOptions?.length;
  const hasSkipCheckOriginalName = isIP || isUR || isFIUR;

  if (ApplCondSubLength) {
    let foundApplCondSubType: string = '';

    switch (item.organizationType?.label) {
      case KnmSubjectTypesEnum.FIZ:
        foundApplCondSubType = 'FL';
        break;
      case KnmSubjectTypesEnum.UR:
        foundApplCondSubType = 'UL';
        break;
      case KnmSubjectTypesEnum.IP:
        foundApplCondSubType = 'IP';
        break;
      case KnmSubjectTypesEnum.IFIZ:
        foundApplCondSubType = 'IFL';
        break;
      case KnmSubjectTypesEnum.IUR:
        foundApplCondSubType = 'IUL';
        break;
      case KnmSubjectTypesEnum.FIUR:
        foundApplCondSubType = 'FIUL';
        break;
      default:
        break;
    }

    foundApplCondSubject = applicationConditionSubjectsOptions.find(
      subItem =>
        subItem.subjectType === foundApplCondSubType &&
        ((item.inn && subItem.inn === item.inn) ||
          (item.ogrn && subItem.ogrn === item.ogrn) ||
          (item.snils && subItem.snils === item.snils) ||
          (item.originalName &&
            (subItem.organizationFullName === item.originalName ||
              (item.originalName.toLocaleLowerCase().indexOf((subItem.firstName || '').toLocaleLowerCase()) > -1 &&
                item.originalName.toLocaleLowerCase().indexOf((subItem.surName || '').toLocaleLowerCase()) > -1))) ||
          (item.rafpNza && subItem.rafp === item.rafpNza) ||
          (item.kpp && subItem.kpp === item.kpp)),
    );
  }

  if (
    isUse5234153 &&
    ApplCondSubLength &&
    item.organizationType?.label &&
    (item.inn || item.ogrn || item.snils || item.originalName || item.rafpNza || item.kpp) &&
    !foundApplCondSubject
  ) {
    return APPLICATION_CONDITION_SUBJECT_REQUIRED;
  }

  const resultDecisionsValidatorOptions = {
    settings,
    state,
    values,
    isDecisionStatusFillInProcess,
    isDecisionStatusReadyForApprove,
    isLater4111ForDecision,
    isNewDecision,
    isSupervisor,
    isWatchOnly,
  };

  return {
    organizationType: composeValidators(makeRequired(REQUIRED_ERROR))(item.organizationType),
    isPresentInItRegistry:
      reasonRequirements?.it !== null &&
      ((reasonRequirements?.it && item.isPresentInItRegistry === false) ||
        (!reasonRequirements?.it && item.isPresentInItRegistry === true))
        ? CHECKING_IT_ACCREDITATION
        : undefined,
    ogrn:
      item.organizationType && !isFIZ
        ? composeValidators(
            makeIsNormalInteger(UNALLOWED_CHARACTERS_ERROR),
            makeMaxCharactersValidator(32, MAX_CHARACTERS_ERROR),
            makeIsOgrn(OGRN_LENGTH_ERROR),
          )(item.ogrn)
        : undefined,
    inn: composeValidators(
      !item.rafpNza && !isIUR && !isIFIZ && !item.snils ? makeRequired(REQUIRED_ERROR) : () => undefined,
      item.inn ? makeIsNormalInteger(UNALLOWED_CHARACTERS_ERROR) : () => undefined,
      item.inn
        ? makeIsErknmInn({
            errorMessage: INN_ERROR(),
            getIsFiz: () => isFIZ,
            getIsRequired: () => true, // kindKnm?.code !== KnmKindCodesEnum.RO
          })
        : () => undefined,
    )(item.inn),
    rafpNza: composeValidators(
      isFIUR && !item.inn && !item.kpp ? makeRequired(REQUIRED_ERROR) : () => undefined,
      isFIUR && !item.inn && !item.kpp ? makeIsNormalInteger(UNALLOWED_CHARACTERS_ERROR) : () => undefined,
      isFIUR && !item.inn && !item.kpp
        ? isDigitsCountCorrect({
            errorMessage: RAFP_NZA_ERROR,
            digitsCount: 11,
          })
        : () => undefined,
      isFIUR && !item.inn && !item.kpp ? validateRafpNzaStatus() : () => undefined,
    )(item.rafpNza),
    kpp: composeValidators(
      isFIUR && !item.rafpNza ? makeRequired(REQUIRED_ERROR) : () => undefined,
      isFIUR && !item.rafpNza ? makeIsNormalInteger(UNALLOWED_CHARACTERS_ERROR) : () => undefined,
      isFIUR && !item.rafpNza
        ? isDigitsCountCorrect({
            errorMessage: KPP_ERROR,
            digitsCount: 9,
          })
        : () => undefined,
    )(item.kpp),
    originalName: composeValidators(
      makeRequired(
        REQUIRED_ERROR,
        () => (!(isOrganizationNameFieldEnabledWithoutFiz(isFIZ) && isFIZ) && !isIFIZ) || hasSkipCheckOriginalName,
      ),
      makeMinCharactersValidator(3, REQUIRED_ERROR, () => hasSkipCheckOriginalName),
      makeMaxCharactersValidator(1024, MAX_CHARACTERS_ERROR),
    )(item.originalName),
    snils: composeValidators(
      !item.inn && isFIZ ? makeRequired(REQUIRED_ERROR) : () => undefined,
      makeSnilsValidation('Поле заполнено некорректно', () => !item.snils),
    )(item.snils),
    country: makeRequired(REQUIRED_ERROR, () => !isIUR && !isIFIZ)(item.country),
    document: composeValidators(
      makeRequired(REQUIRED_ERROR, () => !isIFIZ),
      makeMinCharactersValidator(3, MIN_CHARACTERS_ERROR(3), () => !isIFIZ),
      makeMaxCharactersValidator(1000, MAX_CHARACTERS_ERROR, () => !isIFIZ),
    )(item.document),
    act: !isFounderValidate
      ? getDecisionActValidate(
          item.act,
          basisDocumentDateTime,
          isUse4151,
          isUseEvidence,
          isEnablePrescriptionCheck && isRepeatValidationChecking && !isDecisionStatusFillInProcess,
          values?.events,
        )
      : undefined,

    resultDecisions: !isFounderValidate
      ? getDecisionResultDecisionValidator(
          item.resultDecisions,
          basisDocumentDateTime,
          reasons,
          isUse5234153,
          resultDecisionsValidatorOptions,
        )
      : undefined,
  };
};
